import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, FileText, Building2, Shield, Headphones, UserCog, Monitor, Users, UserCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [customNavPages, setCustomNavPages] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const fetchNavPages = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/custom-pages`);
        setCustomNavPages(res.data.filter(p => p.show_in_nav));
      } catch { /* silent */ }
    };
    fetchNavPages();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); setServicesOpen(false); }, [location]);

  const isActive = (path) => location.pathname === path;

  const services = [
    { name: "Data Entry & Processing", path: "/services/data-entry", icon: FileText },
    { name: "Accounts Payable / Receivable", path: "/services/accounts-payable-receivable", icon: Building2 },
    { name: "Back Office Support", path: "/services/back-office-support", icon: Shield },
    { name: "Customer Support", path: "/services/customer-support", icon: Headphones },
    { name: "HR Outsourcing", path: "/services/hr-outsourcing", icon: UserCog },
    { name: "IT Outsourcing", path: "/services/it-outsourcing", icon: Monitor },
    { name: "Virtual Employee", path: "/services/virtual-employee", icon: UserCheck },
  ];

  const digitalServices = [
    { name: "SEO Services", path: "/services/seo", icon: FileText },
    { name: "Web Development", path: "/services/web-development", icon: Monitor },
    { name: "PPC Advertising", path: "/services/ppc", icon: Building2 },
    { name: "Social Media Marketing", path: "/services/social-media", icon: Users },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-200' : 'bg-white/70 backdrop-blur-lg'}`} data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_instant-seo-score/artifacts/h319f9hx_Adsolute.png" alt="Adsolute" className="h-9 w-auto" width="140" height="36" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className={`text-sm font-medium transition-colors ${isActive('/') ? 'text-[#0047AB]' : 'text-slate-600 hover:text-[#0F172A]'}`} data-testid="nav-home">Home</Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <button className="text-sm font-medium text-slate-600 hover:text-[#0F172A] flex items-center gap-1 transition-colors" data-testid="nav-services-dropdown">
                Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 py-2" style={{ width: '520px' }}>
                    <div className="grid grid-cols-2">
                      <div className="p-2 border-r border-slate-100">
                        <p className="px-3 py-2 text-xs uppercase tracking-wider text-slate-400 font-semibold">Outsourcing</p>
                        {services.map((svc) => (
                          <Link key={svc.path} to={svc.path} className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${isActive(svc.path) ? 'text-[#0047AB] bg-blue-50' : 'text-slate-600 hover:text-[#0F172A] hover:bg-slate-50'}`} data-testid={`nav-service-${svc.path.split('/').pop()}`}>
                            <svc.icon className="w-4 h-4 text-[#0047AB]" />
                            {svc.name}
                          </Link>
                        ))}
                      </div>
                      <div className="p-2">
                        <p className="px-3 py-2 text-xs uppercase tracking-wider text-slate-400 font-semibold">Digital Marketing</p>
                        {digitalServices.map((svc) => (
                          <Link key={svc.path} to={svc.path} className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${isActive(svc.path) ? 'text-[#0047AB] bg-blue-50' : 'text-slate-600 hover:text-[#0F172A] hover:bg-slate-50'}`} data-testid={`nav-dm-${svc.path.split('/').pop()}`}>
                            <svc.icon className="w-4 h-4 text-[#0047AB]" />
                            {svc.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/blog" className={`text-sm font-medium transition-colors ${isActive('/blog') ? 'text-[#0047AB]' : 'text-slate-600 hover:text-[#0F172A]'}`} data-testid="nav-blog">Blog</Link>

            {customNavPages.map((cp) => (
              <Link key={cp.slug} to={`/page/${cp.slug}`} className={`text-sm font-medium transition-colors ${isActive(`/page/${cp.slug}`) ? 'text-[#0047AB]' : 'text-slate-600 hover:text-[#0F172A]'}`} data-testid={`nav-custom-${cp.slug}`}>{cp.title}</Link>
            ))}

            <Link to="/contact" className="btn-primary text-sm py-2.5 px-5" data-testid="nav-contact">Get Started</Link>
          </div>

          {/* Mobile Toggle */}
          <button className="lg:hidden text-slate-600" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
            <div className="px-6 py-4 space-y-1">
              <Link to="/" className="block py-2.5 text-slate-700 font-medium" onClick={() => setIsOpen(false)}>Home</Link>
              <p className="text-xs uppercase tracking-wider text-slate-400 pt-2 pb-1 font-semibold">Outsourcing</p>
              {services.map((svc) => (
                <Link key={svc.path} to={svc.path} className="block py-2 pl-3 text-slate-600 text-sm" onClick={() => setIsOpen(false)}>{svc.name}</Link>
              ))}
              <p className="text-xs uppercase tracking-wider text-slate-400 pt-3 pb-1 font-semibold">Digital Marketing</p>
              {digitalServices.map((svc) => (
                <Link key={svc.path} to={svc.path} className="block py-2 pl-3 text-slate-600 text-sm" onClick={() => setIsOpen(false)}>{svc.name}</Link>
              ))}
              <Link to="/blog" className="block py-2.5 text-slate-700 font-medium" onClick={() => setIsOpen(false)}>Blog</Link>
              {customNavPages.map((cp) => (
                <Link key={cp.slug} to={`/page/${cp.slug}`} className="block py-2.5 text-slate-700 font-medium" onClick={() => setIsOpen(false)}>{cp.title}</Link>
              ))}
              <Link to="/contact" className="block py-2.5 text-slate-700 font-medium" onClick={() => setIsOpen(false)}>Contact</Link>
              <Link to="/contact" className="btn-primary w-full justify-center mt-3" onClick={() => setIsOpen(false)}>Get Started</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
